/**
 * Portal Members Page — manage org members (for managers/owners)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import type { ClientRole } from '../../../shared/types/auth';
import './portal-responsive.css';

const ROLE_HIERARCHY: Record<string, number> = { viewer: 0, commenter: 1, people_manager: 2, client_admin: 3 };
const ROLE_OPTIONS: ClientRole[] = ['viewer', 'commenter', 'people_manager', 'client_admin'];
const ASSIGNABLE_ROLES: ClientRole[] = ['viewer', 'commenter', 'people_manager'];

const ROLE_LABELS: Record<string, string> = {
  client_admin: 'Client Admin',
  people_manager: 'People Manager',
  commenter: 'User / Commenter',
  viewer: 'General Viewer',
};

const roleBadgeColors: Record<string, { bg: string; color: string }> = {
  client_admin: { bg: '#4c1d95', color: '#c4b5fd' },
  people_manager: { bg: '#1e3a5f', color: '#93c5fd' },
  commenter: { bg: '#1a2e1a', color: '#86efac' },
  viewer: { bg: '#374151', color: '#9ca3af' },
};

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let result = '';
  for (let i = 0; i < 10; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
}

export function PortalMembersPage() {
  const { clientMemberships, user, isAdmin } = useAuth();
  const [members, setMembers] = useState<any[]>([]);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [memberLimit, setMemberLimit] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'active' | 'pending'>('active');

  // Add member modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', clientRole: 'viewer' as ClientRole });
  const [addError, setAddError] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addSuccess, setAddSuccess] = useState<{ email: string; password: string } | null>(null);
  const [addPasswordCopied, setAddPasswordCopied] = useState(false);

  // Reset password modal
  const [resetTarget, setResetTarget] = useState<{ userId: string; name: string; email: string } | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetPasswordCopied, setResetPasswordCopied] = useState(false);

  // CSV import modal
  const [showCsvModal, setShowCsvModal] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Use first client membership
  const membership = clientMemberships[0];
  const clientId = membership?.clientId;
  const myRole = membership?.role;
  const isManager = isAdmin || (myRole && ROLE_HIERARCHY[myRole] >= ROLE_HIERARCHY['people_manager']);
  const isClientAdmin = isAdmin || myRole === 'client_admin';

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const membersData = await api.portal.getMembers(clientId);
      setMembers(membersData.members.filter((m: any) => m.status === 'active'));
      setMemberLimit(membersData.memberLimit);

      if (isManager) {
        const pending = await api.portal.getPendingUsers(clientId);
        setPendingUsers(pending);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId, isManager]);

  useEffect(() => { load(); }, [load]);

  const approveUser = async (userId: string) => {
    if (!clientId) return;
    try {
      await api.portal.approveUser(clientId, userId);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const rejectUser = async (userId: string) => {
    if (!clientId) return;
    try {
      await api.portal.rejectUser(clientId, userId);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const changeRole = async (userId: string, role: ClientRole) => {
    if (!clientId) return;
    try {
      await api.portal.updateMemberRole(clientId, userId, role);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  // ─── Add Member ────────────────────────────────────────────────────────────

  const openAddModal = () => {
    setAddForm({ name: '', email: '', password: generatePassword(), clientRole: 'viewer' });
    setAddError('');
    setAddSuccess(null);
    setAddPasswordCopied(false);
    setShowAddModal(true);
  };

  const handleAddUser = async () => {
    if (!clientId) return;
    if (!addForm.email.trim()) { setAddError('Email is required'); return; }
    if (!addForm.password || addForm.password.length < 8) { setAddError('Password must be at least 8 characters'); return; }

    setAddSaving(true);
    setAddError('');
    try {
      await api.portal.addUser(clientId, {
        email: addForm.email.trim(),
        name: addForm.name.trim(),
        password: addForm.password,
        clientRole: addForm.clientRole,
      });
      setAddSuccess({ email: addForm.email.trim(), password: addForm.password });
      load();
    } catch (err: any) {
      setAddError(err?.message || 'Failed to add member');
    } finally {
      setAddSaving(false);
    }
  };

  // ─── Reset Password ───────────────────────────────────────────────────────

  const openResetModal = (m: any) => {
    const pw = generatePassword();
    setResetTarget({ userId: m.userId, name: m.name || m.email, email: m.email });
    setResetPassword(pw);
    setResetError('');
    setResetSuccess(false);
    setResetPasswordCopied(false);
  };

  const handleResetPassword = async () => {
    if (!clientId || !resetTarget) return;
    if (!resetPassword || resetPassword.length < 8) { setResetError('Password must be at least 8 characters'); return; }

    setResetSaving(true);
    setResetError('');
    try {
      await api.portal.resetMemberPassword(clientId, resetTarget.userId, resetPassword);
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(err?.message || 'Failed to reset password');
    } finally {
      setResetSaving(false);
    }
  };

  // ─── Copy helpers ──────────────────────────────────────────────────────────

  const copyToClipboard = async (text: string, setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  if (!clientId) {
    return <div style={{ color: '#94a3b8' }}>No organization membership found.</div>;
  }

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading...</div>;

  const activeCount = members.length;
  const atLimit = memberLimit ? activeCount >= memberLimit : false;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 16 : 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ color: '#64748b', fontSize: 14, fontWeight: 400 }}>
          {activeCount} active{memberLimit ? ` / ${memberLimit} limit` : ''}
        </div>
        {isClientAdmin && (
          <div style={{ display: 'flex', gap: 8, ...(isMobile ? { width: '100%' } : {}) }}>
            <button
              onClick={openAddModal}
              disabled={atLimit}
              title={atLimit ? 'Member limit reached' : undefined}
              style={{
                background: atLimit ? '#1e293b' : '#4F7CFF',
                color: atLimit ? '#475569' : '#fff',
                border: 'none',
                borderRadius: 8,
                padding: isMobile ? '10px 20px' : '8px 16px',
                fontSize: isMobile ? 14 : 13,
                fontWeight: 600,
                cursor: atLimit ? 'not-allowed' : 'pointer',
                ...(isMobile ? { minHeight: 44, flex: 1 } : {}),
              }}
            >
              + Add Member
            </button>
            <button
              onClick={() => setShowCsvModal(true)}
              disabled={atLimit}
              style={{
                ...styles.btnOutline,
                ...(isMobile ? { minHeight: 44, flex: 1 } : {}),
              }}
            >
              Import CSV
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      {isManager && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
          <TabButton active={tab === 'active'} onClick={() => setTab('active')} label="Active" count={members.length} isMobile={isMobile} />
          <TabButton active={tab === 'pending'} onClick={() => setTab('pending')} label="Pending" count={pendingUsers.length} isMobile={isMobile} />
        </div>
      )}

      {tab === 'active' && (
        <section style={{ ...styles.section, ...(isMobile ? { padding: 12 } : {}) }}>
          {members.map((m: any) => (
            <div key={m.userId} style={{
              ...styles.row,
              ...(isMobile ? {
                flexDirection: 'column' as const,
                alignItems: 'flex-start',
                gap: 10,
                padding: '14px 0',
              } : {}),
            }}>
              <div style={{ flex: 1, ...(isMobile ? { width: '100%' } : {}) }}>
                {isMobile ? (
                  <>
                    <div style={{ color: '#f1f5f9', fontSize: 14 }}>
                      {m.name || m.email}
                      {m.userId === user?.id && <span style={{ color: '#475569', fontSize: 11, marginLeft: 8 }}>(you)</span>}
                    </div>
                    {m.name && <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{m.email}</div>}
                  </>
                ) : (
                  <>
                    <span style={{ color: '#f1f5f9', fontSize: 14 }}>{m.name || m.email}</span>
                    {m.name && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{m.email}</span>}
                    {m.userId === user?.id && <span style={{ color: '#475569', fontSize: 11, marginLeft: 8 }}>(you)</span>}
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...(isMobile ? { width: '100%', flexDirection: 'column' } : {}), flexWrap: 'wrap' }}>
                {isClientAdmin && m.userId !== user?.id ? (
                  <>
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m.userId, e.target.value as ClientRole)}
                      style={{
                        ...styles.roleSelect,
                        ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '8px 12px', width: '100%' } : {}),
                      }}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                      ))}
                    </select>
                    {m.role !== 'client_admin' && (
                      <button
                        onClick={() => openResetModal(m)}
                        style={{
                          background: 'transparent',
                          color: '#64748b',
                          border: isMobile ? '1px solid #475569' : 'none',
                          borderRadius: 6,
                          padding: isMobile ? '10px 14px' : '4px 8px',
                          fontSize: isMobile ? 14 : 12,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          ...(isMobile ? { minHeight: 44, width: '100%' } : {}),
                        }}
                      >
                        Reset Password
                      </button>
                    )}
                  </>
                ) : (
                  <RoleBadge role={m.role} />
                )}
              </div>
            </div>
          ))}
          {members.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 14 }}>No active members</div>
          )}
        </section>
      )}

      {tab === 'pending' && isManager && (
        <section style={{ ...styles.section, ...(isMobile ? { padding: 12 } : {}) }}>
          {pendingUsers.map((u: any) => (
            <div key={u.userId} style={{
              ...styles.row,
              ...(isMobile ? {
                flexDirection: 'column' as const,
                alignItems: 'flex-start',
                gap: 10,
                padding: '14px 0',
              } : {}),
            }}>
              <div style={{ flex: 1, ...(isMobile ? { width: '100%' } : {}) }}>
                {isMobile ? (
                  <>
                    <div style={{ color: '#f1f5f9', fontSize: 14 }}>{u.name || u.email}</div>
                    {u.name && <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{u.email}</div>}
                  </>
                ) : (
                  <>
                    <span style={{ color: '#f1f5f9', fontSize: 14 }}>{u.name || u.email}</span>
                    {u.name && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{u.email}</span>}
                  </>
                )}
              </div>
              <div style={{
                display: 'flex',
                gap: 8,
                ...(isMobile ? { width: '100%' } : {}),
                flexWrap: 'wrap',
              }}>
                <button
                  onClick={() => approveUser(u.userId)}
                  style={{
                    ...styles.approveBtn,
                    ...(isMobile ? { minHeight: 44, flex: 1, fontSize: 14, padding: '10px 14px' } : {}),
                  }}
                >
                  Approve
                </button>
                <button
                  onClick={() => rejectUser(u.userId)}
                  style={{
                    ...styles.rejectBtn,
                    ...(isMobile ? { minHeight: 44, flex: 1, fontSize: 14, padding: '10px 14px' } : {}),
                  }}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
          {pendingUsers.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 14 }}>No pending users</div>
          )}
        </section>
      )}

      {/* ─── Add Member Modal ─────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={styles.overlay} onClick={() => { if (!addSaving) setShowAddModal(false); }}>
          <div style={{ ...styles.modal, ...(isMobile ? { width: '95vw', padding: 20 } : {}) }} onClick={(e) => e.stopPropagation()}>
            {addSuccess ? (
              <>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: '0 0 16px 0' }}>Member Added</h2>
                <div style={{ background: 'rgba(22,101,52,0.2)', border: '1px solid #166534', color: '#86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 }}>
                  {addSuccess.email} has been added to your organization.
                </div>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#94a3b8' }}>Temporary Password</div>
                <div style={{
                  background: '#0d0d0d',
                  border: '1px solid #333333',
                  borderRadius: 8,
                  padding: '14px 16px',
                  fontFamily: 'monospace',
                  fontSize: isMobile ? 22 : 18,
                  color: '#f1f5f9',
                  letterSpacing: '0.1em',
                  textAlign: 'center',
                  marginBottom: 8,
                  userSelect: 'all',
                }}>
                  {addSuccess.password}
                </div>
                <button
                  onClick={() => copyToClipboard(addSuccess.password, setAddPasswordCopied)}
                  style={{
                    background: addPasswordCopied ? '#166534' : '#4F7CFF',
                    color: addPasswordCopied ? '#86efac' : '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    width: '100%',
                    minHeight: 44,
                    marginBottom: 8,
                  }}
                >
                  {addPasswordCopied ? 'Copied!' : 'Copy Password'}
                </button>
                <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 16 }}>
                  This password will not be shown again. The user will be asked to change it on first login.
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{ ...styles.btnOutline, width: '100%', minHeight: 44 }}
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Add Member</h2>
                  <button onClick={() => setShowAddModal(false)} style={styles.closeBtn}>&times;</button>
                </div>

                {addError && (
                  <div style={{ background: 'rgba(127,29,29,0.2)', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                    {addError}
                  </div>
                )}

                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Full name"
                  style={{ ...styles.input, ...(isMobile ? { fontSize: 16 } : {}) }}
                />

                <label style={styles.label}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  placeholder="email@example.com"
                  style={{ ...styles.input, ...(isMobile ? { fontSize: 16 } : {}) }}
                />

                <label style={styles.label}>Password <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                  <input
                    type="text"
                    value={addForm.password}
                    onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                    style={{ ...styles.input, marginBottom: 0, flex: 1, ...(isMobile ? { fontSize: 16 } : {}) }}
                  />
                  <button
                    onClick={() => setAddForm({ ...addForm, password: generatePassword() })}
                    style={{ ...styles.btnOutline, ...(isMobile ? { minHeight: 44 } : {}) }}
                  >
                    Generate
                  </button>
                </div>

                <label style={styles.label}>Role</label>
                <select
                  value={addForm.clientRole}
                  onChange={(e) => setAddForm({ ...addForm, clientRole: e.target.value as ClientRole })}
                  style={{ ...styles.input, marginBottom: 24, ...(isMobile ? { fontSize: 16, minHeight: 44 } : {}) }}
                >
                  {ASSIGNABLE_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>

                <div style={{
                  display: 'flex',
                  gap: 10,
                  flexDirection: isMobile ? 'column-reverse' : 'row',
                  justifyContent: 'flex-end',
                }}>
                  <button
                    onClick={() => setShowAddModal(false)}
                    disabled={addSaving}
                    style={{ ...styles.btnOutline, ...(isMobile ? { minHeight: 44, width: '100%' } : {}) }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddUser}
                    disabled={addSaving}
                    style={{
                      background: addSaving ? '#1e293b' : '#4F7CFF',
                      color: addSaving ? '#475569' : '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: addSaving ? 'not-allowed' : 'pointer',
                      ...(isMobile ? { minHeight: 44, width: '100%' } : {}),
                    }}
                  >
                    {addSaving ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Reset Password Modal ─────────────────────────────────────────── */}
      {resetTarget && (
        <div style={styles.overlay} onClick={() => { if (!resetSaving) setResetTarget(null); }}>
          <div style={{ ...styles.modal, ...(isMobile ? { width: '95vw', padding: 20 } : {}) }} onClick={(e) => e.stopPropagation()}>
            {resetSuccess ? (
              <>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: '0 0 16px 0' }}>Password Reset</h2>
                <div style={{ background: 'rgba(22,101,52,0.2)', border: '1px solid #166534', color: '#86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 }}>
                  Password has been reset for {resetTarget.name}.
                </div>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#94a3b8' }}>New Temporary Password</div>
                <div style={{
                  background: '#0d0d0d',
                  border: '1px solid #333333',
                  borderRadius: 8,
                  padding: '14px 16px',
                  fontFamily: 'monospace',
                  fontSize: isMobile ? 22 : 18,
                  color: '#f1f5f9',
                  letterSpacing: '0.1em',
                  textAlign: 'center',
                  marginBottom: 8,
                  userSelect: 'all',
                }}>
                  {resetPassword}
                </div>
                <button
                  onClick={() => copyToClipboard(resetPassword, setResetPasswordCopied)}
                  style={{
                    background: resetPasswordCopied ? '#166534' : '#4F7CFF',
                    color: resetPasswordCopied ? '#86efac' : '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    width: '100%',
                    minHeight: 44,
                    marginBottom: 8,
                  }}
                >
                  {resetPasswordCopied ? 'Copied!' : 'Copy Password'}
                </button>
                <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 16 }}>
                  This password will not be shown again. The user will be asked to change it on next login.
                </div>
                <button
                  onClick={() => setResetTarget(null)}
                  style={{ ...styles.btnOutline, width: '100%', minHeight: 44 }}
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Reset Password</h2>
                  <button onClick={() => setResetTarget(null)} style={styles.closeBtn}>&times;</button>
                </div>

                <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 16 }}>
                  Reset password for <span style={{ color: '#f1f5f9', fontWeight: 500 }}>{resetTarget.name}</span>
                  {resetTarget.name !== resetTarget.email && (
                    <span style={{ color: '#64748b' }}> ({resetTarget.email})</span>
                  )}
                </div>

                {resetError && (
                  <div style={{ background: 'rgba(127,29,29,0.2)', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                    {resetError}
                  </div>
                )}

                <label style={styles.label}>New Password</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexDirection: isMobile ? 'column' : 'row' }}>
                  <input
                    type="text"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    style={{ ...styles.input, marginBottom: 0, flex: 1, ...(isMobile ? { fontSize: 16 } : {}) }}
                  />
                  <button
                    onClick={() => setResetPassword(generatePassword())}
                    style={{ ...styles.btnOutline, ...(isMobile ? { minHeight: 44 } : {}) }}
                  >
                    Generate
                  </button>
                </div>

                <div style={{
                  display: 'flex',
                  gap: 10,
                  flexDirection: isMobile ? 'column-reverse' : 'row',
                  justifyContent: 'flex-end',
                }}>
                  <button
                    onClick={() => setResetTarget(null)}
                    disabled={resetSaving}
                    style={{ ...styles.btnOutline, ...(isMobile ? { minHeight: 44, width: '100%' } : {}) }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={resetSaving}
                    style={{
                      background: resetSaving ? '#1e293b' : '#4F7CFF',
                      color: resetSaving ? '#475569' : '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: resetSaving ? 'not-allowed' : 'pointer',
                      ...(isMobile ? { minHeight: 44, width: '100%' } : {}),
                    }}
                  >
                    {resetSaving ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── CSV Import Modal ─────────────────────────────────────────────── */}
      {showCsvModal && clientId && (
        <CsvImportModal
          clientId={clientId}
          isMobile={isMobile}
          onClose={() => setShowCsvModal(false)}
          onImported={() => { setShowCsvModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── CSV Import Modal ──────────────────────────────────────────────────────

const PORTAL_CSV_HEADERS = ['email', 'password', 'name', 'org_role'];
const PORTAL_CSV_TEMPLATE = [
  PORTAL_CSV_HEADERS.join(',') + ',,REFERENCE',
  'john@example.com,SecurePass123!,John Doe,viewer,,org_role values: viewer | commenter | people_manager',
  'jane@example.com,SecurePass456!,Jane Smith,commenter,,',
  'bob@example.com,SecurePass789!,Bob Manager,people_manager,,',
].join('\n') + '\n';

function CsvImportModal({ clientId, isMobile, onClose, onImported }: { clientId: string; isMobile: boolean; onClose: () => void; onImported: () => void }) {
  const [rows, setRows] = useState<Array<{ email: string; password: string; name: string; orgRole: string }>>([]);
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<Array<{ email: string; success: boolean; error?: string }> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const downloadTemplate = () => {
    const blob = new Blob([PORTAL_CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'member-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string) => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      setParseError('CSV must have a header row and at least one data row');
      return;
    }

    const header = lines[0].toLowerCase().split(',').map((h) => h.trim());
    const emailIdx = header.indexOf('email');
    const passwordIdx = header.indexOf('password');
    const nameIdx = header.indexOf('name');
    const orgRoleIdx = header.findIndex((h) => h === 'org_role' || h === 'org role' || h === 'orgrole' || h === 'role');

    if (emailIdx === -1 || passwordIdx === -1) {
      setParseError('CSV must have "email" and "password" columns');
      return;
    }

    const parsed = [];
    const errors = [];
    for (let i = 1; i < lines.length; i++) {
      const cols: string[] = [];
      let current = '';
      let inQuotes = false;
      for (const ch of lines[i]) {
        if (ch === '"') { inQuotes = !inQuotes; continue; }
        if (ch === ',' && !inQuotes) { cols.push(current.trim()); current = ''; continue; }
        current += ch;
      }
      cols.push(current.trim());

      const email = cols[emailIdx] || '';
      const password = cols[passwordIdx] || '';

      if (!email) { errors.push(`Row ${i + 1}: missing email`); continue; }
      if (!password) { errors.push(`Row ${i + 1}: missing password for ${email}`); continue; }

      const orgRole = orgRoleIdx >= 0 ? (cols[orgRoleIdx] || 'viewer') : 'viewer';
      if (orgRole === 'client_admin') {
        errors.push(`Row ${i + 1}: cannot assign client_admin role`);
        continue;
      }

      parsed.push({
        email,
        password,
        name: nameIdx >= 0 ? (cols[nameIdx] || '') : '',
        orgRole,
      });
    }

    if (errors.length > 0) setParseError(errors.join('; '));
    else setParseError('');
    setRows(parsed);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => parseCSV(reader.result as string);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await api.portal.bulkAddUsers(clientId, rows.map((r) => ({
        email: r.email,
        password: r.password,
        name: r.name || undefined,
        orgRole: r.orgRole || undefined,
      })));
      setResults(result.results);
      if (result.created > 0) {
        setTimeout(onImported, 2000);
      }
    } catch (err: any) {
      setParseError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={{ ...styles.modal, width: 600, ...(isMobile ? { width: '95vw', padding: 20 } : {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>Import Members from CSV</h2>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>

        {results ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <span style={{ color: '#86efac', fontWeight: 600, fontSize: 14 }}>
                {results.filter((r) => r.success).length} added
              </span>
              {results.some((r) => !r.success) && (
                <span style={{ color: '#fca5a5', fontWeight: 600, fontSize: 14, marginLeft: 12 }}>
                  {results.filter((r) => !r.success).length} failed
                </span>
              )}
            </div>
            <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #333', borderRadius: 10, maxHeight: 300, overflowY: 'auto' }}>
              {results.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid #262626', fontSize: 13 }}>
                  <span style={{ color: '#f1f5f9' }}>{r.email}</span>
                  {r.success ? (
                    <span style={{ color: '#86efac', fontSize: 12 }}>Added</span>
                  ) : (
                    <span style={{ color: '#fca5a5', fontSize: 12 }}>{r.error}</span>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={{ background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', ...(isMobile ? { minHeight: 44, width: '100%' } : {}) }}>
                Done
              </button>
            </div>
            <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 12 }}>
              All imported users will be required to change their password on first login.
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              <button onClick={downloadTemplate} style={{ ...styles.btnOutline, ...(isMobile ? { minHeight: 44, flex: 1 } : {}) }}>
                Download Template
              </button>
              <button onClick={() => fileRef.current?.click()} style={{ background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', ...(isMobile ? { minHeight: 44, flex: 1 } : {}) }}>
                Upload CSV
              </button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFileSelect} style={{ display: 'none' }} />
            </div>

            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
              Columns: <strong style={{ color: '#94a3b8' }}>email</strong> (required), <strong style={{ color: '#94a3b8' }}>password</strong> (required), <strong style={{ color: '#94a3b8' }}>name</strong>, <strong style={{ color: '#94a3b8' }}>org_role</strong> (viewer / commenter / people_manager)
            </div>

            {parseError && (
              <div style={{ background: 'rgba(127,29,29,0.2)', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
                {parseError}
              </div>
            )}

            {rows.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 8 }}>
                  Preview ({rows.length} member{rows.length > 1 ? 's' : ''})
                </div>
                <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #333', borderRadius: 10, maxHeight: 250, overflowY: 'auto', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #333' }}>
                        {['Email', 'Name', 'Role'].map((h) => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #262626' }}>
                          <td style={{ padding: '6px 12px', color: '#f1f5f9' }}>{r.email}</td>
                          <td style={{ padding: '6px 12px', color: '#94a3b8' }}>{r.name || '—'}</td>
                          <td style={{ padding: '6px 12px', color: '#94a3b8' }}>{ROLE_LABELS[r.orgRole] || r.orgRole}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{
                  display: 'flex', gap: 10, marginTop: 16,
                  flexDirection: isMobile ? 'column-reverse' : 'row',
                  justifyContent: 'flex-end',
                }}>
                  <button onClick={onClose} style={{ ...styles.btnOutline, ...(isMobile ? { minHeight: 44, width: '100%' } : {}) }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importing || rows.length === 0}
                    style={{
                      background: importing ? '#1e293b' : '#4F7CFF',
                      color: importing ? '#475569' : '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: importing ? 'not-allowed' : 'pointer',
                      ...(isMobile ? { minHeight: 44, width: '100%' } : {}),
                    }}
                  >
                    {importing ? 'Importing...' : `Import ${rows.length} Member${rows.length > 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label, count, isMobile }: { active: boolean; onClick: () => void; label: string; count: number; isMobile?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? '#1e293b' : 'transparent',
        color: active ? '#f1f5f9' : '#64748b',
        border: '1px solid',
        borderColor: active ? '#334155' : 'transparent',
        borderRadius: 6,
        padding: isMobile ? '10px 18px' : '6px 14px',
        fontSize: isMobile ? 14 : 13,
        fontWeight: 500,
        cursor: 'pointer',
        ...(isMobile ? { minHeight: 44 } : {}),
      }}
    >
      {label} {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
    </button>
  );
}

function RoleBadge({ role }: { role: string }) {
  const colors = roleBadgeColors[role] || roleBadgeColors.viewer;
  return (
    <span style={{
      background: colors.bg,
      color: colors.color,
      padding: '3px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
    }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    padding: 20,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #262626',
  },
  roleSelect: {
    background: '#0d0d0d',
    border: '1px solid #333333',
    borderRadius: 6,
    padding: '4px 8px',
    color: '#f1f5f9',
    fontSize: 12,
    outline: 'none',
    textTransform: 'capitalize',
  },
  approveBtn: {
    background: '#166534',
    color: '#86efac',
    border: 'none',
    borderRadius: 6,
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  rejectBtn: {
    background: '#7f1d1d',
    color: '#fca5a5',
    border: 'none',
    borderRadius: 6,
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    padding: 24,
    width: 480,
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  label: {
    display: 'block',
    fontSize: 13,
    color: '#94a3b8',
    marginBottom: 6,
    marginTop: 0,
  },
  input: {
    display: 'block',
    width: '100%',
    background: '#0d0d0d',
    border: '1px solid #333333',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#f1f5f9',
    fontSize: 14,
    outline: 'none',
    marginBottom: 16,
    boxSizing: 'border-box' as const,
  },
  btnOutline: {
    background: 'transparent',
    color: '#94a3b8',
    border: '1px solid #475569',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: 24,
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
};
